import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Trash2, Search, RefreshCw, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { toast } from "sonner";
import type { RenataMemoryFact, RenataWorkingMemory } from "@/lib/renata/memory/types";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/renata/memory")({
  component: RenataMemoryPage,
  head: () => ({ meta: [{ title: "Memoria del negocio — Renata" }] }),
});

type StatsPayload = {
  stats: {
    byType: Array<{ memory_type: string; count: number; uses: number; last_updated: string }>;
    events30d: { hits: number; misses: number; created: number; used: number };
    workingOpen: number;
    conversations: number;
  };
  working: RenataWorkingMemory[];
};

function RenataMemoryPage() {
  const tid = useWorkspaceTenantId();
  const [items, setItems] = useState<RenataMemoryFact[]>([]);
  const { confirm } = useAppDialogs();
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<StatsPayload | null>(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState<"all" | "business" | "semantic">("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<RenataMemoryFact | null>(null);
  const [editValue, setEditValue] = useState("");

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const headers = clientRequestHeaders(tid);
      const [listRes, statsRes] = await Promise.all([
        fetch(`/api/renata/memory?type=${type}&q=${encodeURIComponent(q)}&limit=100`, {
          credentials: "include",
          headers,
        }),
        fetch(`/api/renata/memory?stats=1`, { credentials: "include", headers }),
      ]);
      if (!listRes.ok) throw new Error("list");
      const listData = (await listRes.json()) as { items: RenataMemoryFact[]; total: number };
      setItems(listData.items ?? []);
      setTotal(listData.total ?? 0);
      if (statsRes.ok) setStats((await statsRes.json()) as StatsPayload);
    } catch {
      toast.error("No se pudo cargar la memoria");
    } finally {
      setLoading(false);
    }
  }, [tid, type, q]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveEdit() {
    if (!tid || !editing) return;
    const res = await fetch("/api/renata/memory", {
      method: "PATCH",
      credentials: "include",
      headers: { ...clientRequestHeaders(tid), "Content-Type": "application/json" },
      body: JSON.stringify({ id: editing.id, fact_value: editValue }),
    });
    if (!res.ok) {
      toast.error("No se pudo editar");
      return;
    }
    setEditing(null);
    toast.success("Memoria del negocio actualizada");
    void load();
  }

  async function forget(id: string) {
    if (!tid) return;
    if (!(await confirm({ title: "¿Olvidar este hecho?", destructive: true, confirmLabel: "Olvidar" }))) return;
    const res = await fetch(`/api/renata/memory?id=${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
      headers: clientRequestHeaders(tid),
    });
    if (!res.ok) {
      toast.error("No se pudo eliminar");
      return;
    }
    toast.success("Hecho olvidado");
    void load();
  }

  const hits = stats?.stats.events30d.hits ?? 0;
  const misses = stats?.stats.events30d.misses ?? 0;
  const hitRatio = hits + misses > 0 ? Math.round((hits / (hits + misses)) * 100) : null;

  return (
    <div className="flex min-w-0 flex-col">
      <div className="shrink-0 border-b px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-base font-semibold">
              <Brain className="h-4 w-4 text-violet-600" />
              Memoria del negocio operativa
            </h1>
            <p className="text-xs text-muted-foreground">
              Hechos estables del negocio. Conversación = historial; en curso = tarea abierta; negocio/semántica =
              persistente.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => void load()} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            Actualizar
          </Button>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          <Stat label="Conversaciones" value={String(stats?.stats.conversations ?? "—")} />
          <Stat label="Tareas en curso" value={String(stats?.stats.workingOpen ?? "—")} />
          <Stat label="Hechos activos" value={String(total)} />
          <Stat label="Acierto 30d" value={hitRatio != null ? `${hitRatio}%` : "—"} />
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap gap-2 border-b px-4 py-2 sm:px-6">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="h-8 pl-8 text-sm"
            placeholder="Buscar clave, valor, categoría…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void load()}
          />
        </div>
        <select
          className="h-8 rounded-md border bg-background px-2 text-xs"
          value={type}
          onChange={(e) => setType(e.target.value as typeof type)}
        >
          <option value="all">Todos</option>
          <option value="business">Negocio</option>
          <option value="semantic">Semántica</option>
        </select>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Todavía no hay hechos guardados. Cuando digas “siempre / preferimos / recordá”, Renata pedirá aprobación.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((f) => (
              <div key={f.id} className="rounded-lg border px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 dark:bg-violet-950 dark:text-violet-200">
                        {f.memory_type}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{f.category}</span>
                      <code className="text-xs font-semibold">{f.fact_key}</code>
                    </div>
                    {editing?.id === f.id ? (
                      <div className="mt-2 flex gap-2">
                        <Input className="h-8 text-sm" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
                        <Button size="sm" className="h-8" onClick={() => void saveEdit()}>
                          Guardar
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(null)}>
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm">{f.fact_value}</p>
                    )}
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      Usos: {f.use_count}
                      {f.last_used_at ? ` · Último uso: ${new Date(f.last_used_at).toLocaleString("es-AR")}` : ""}
                      {" · "}Creado: {new Date(f.created_at).toLocaleString("es-AR")}
                      {f.created_by ? ` · Por: ${f.created_by.slice(0, 8)}…` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label="Editar hecho"
                      onClick={() => {
                        setEditing(f);
                        setEditValue(f.fact_value);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive"
                      aria-label="Eliminar hecho"
                      onClick={() => void forget(f.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {(stats?.working?.length ?? 0) > 0 && (
          <div className="mt-6">
            <h2 className="mb-2 text-sm font-semibold">Memoria de trabajo en curso</h2>
            <div className="space-y-2">
              {stats!.working.map((w) => (
                <div key={w.id} className="rounded-lg border border-dashed px-3 py-2 text-xs">
                  <p className="font-medium">{w.task_type}</p>
                  <pre className="mt-1 overflow-x-auto text-[10px] text-muted-foreground">
                    {JSON.stringify(w.payload, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
}
